import { action, observable } from 'mobx'
import { ReactNode } from 'react'
import { SxProps, Theme } from '@mui/material'

export type DropdownAlertType =
  | 'info'
  | 'warn'
  | 'error'
  | 'custom'
  | 'success'
  | 'contrast-success'

export const CONTRAST_ALERT_TYPES: DropdownAlertType[] = ['contrast-success']

export interface $MessageData {
  type: DropdownAlertType
  title?: string
  message?: string
  onTap?: () => void
  closeInterval?: number
  icon?: string
  showProgressAnimation?: boolean
  actionComponent?: ReactNode
  sx?: SxProps<Theme>
  priority?: number
}

const MIN_CLOSE_INTERVAL = 5000
const DEFAULT_PRIORITY = 3

class DropdownMessageHandler {
  @observable accessor messageData: Omit<$MessageData, 'actionComponent' | 'sx'> = {
    type: 'custom',
    message: '',
  }
  @observable accessor visible = false
  closeTimer: NodeJS.Timeout | null = null
  actionComponent: ReactNode = null
  sx: SxProps<Theme> | undefined = undefined
  currentPriority = DEFAULT_PRIORITY

  @action showDropdownMessage = (messageData: $MessageData) => {
    const { actionComponent, sx, priority = DEFAULT_PRIORITY, ...rest } = messageData

    if (this.visible && priority > this.currentPriority) return

    this.sx = sx
    this.currentPriority = priority
    const closeInterval = rest.closeInterval && rest.closeInterval > MIN_CLOSE_INTERVAL
      ? rest.closeInterval
      : MIN_CLOSE_INTERVAL
    this.messageData = {
      ...rest,
      closeInterval,
    }
    this.actionComponent = actionComponent ?? null
    this.visible = true

    if (this.closeTimer) {
      clearTimeout(this.closeTimer)
    }

    this.closeTimer = setTimeout(() => {
      this.hideDropdownMessage()
    }, this.messageData.closeInterval)
  }

  @action hideDropdownMessage = () => {
    this.visible = false
    this.currentPriority = DEFAULT_PRIORITY
  }

  onTap = () => {
    const { onTap } = this.messageData
    onTap && onTap()
  }
}

export default DropdownMessageHandler
